import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { PURE_RED } from '@returnmates/client-core/src/constants/colors'
import { LABEL_TYPE_MAP, TRIP_METHOD_MAP } from '@returnmates/client-core/src/constants/trip'
import {
  ClaimReasons,
  LabelType,
  Package,
  PackageStatus,
  Partner,
  Scan,
  TripType,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getBarcodeScan,
  getCarriers,
  getConsolidationByIdSelector,
  getMainRole,
} from '@returnmates/client-core/src/selectors/admin'
import { getConsolidatedByUserSelector } from '@returnmates/client-core/src/selectors/user'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import AutocompleteInput from '@returnmates/ui-core/src/components/AutocompleteInput'
import Input from '@returnmates/ui-core/src/components/Input'
import clsx from 'clsx'
import moment from 'moment-timezone'
import { HTMLAttributes, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, useField } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { cognitoGroups } from '../../../constants/userPermissions'
import useEnabledPartners from '../../../hooks/useEnabledPartners'
import {
  AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION,
  NOT_AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION,
} from '../../../pages/Packages/constants'
import CalendarIcon from '../../images/icons/calendar'
import CheckedSelectorIcon from '../../images/icons/checkedSelector'
import CloseIcon from '../../images/icons/close'
import EraserIcon from '../../images/icons/eraser'
import SelectorIcon from '../../images/icons/selector'
import RemovePackageModal from '../../RemovePackageModal'
import Select from '../../Select'
import SingleDatePicker from '../../SingleDatePicker'
import UploadLabelField from '../../UploadLabelField'
import WrappedSelect from '../../WrappedSelect'
import { CLAIM_REASONS, DIGITAL_LABELS } from '../constants'
import useStyles from '../styles'

interface Props {
  currentPackage: Package | null
}

const Information = ({ currentPackage }: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const userRole = useSelector(getMainRole)
  const barcodeScan = useSelector(getBarcodeScan)
  const packageConsolidation = useSelector(getConsolidationByIdSelector)
  const carriers = useSelector(getCarriers)
  const consolidatedUser = useSelector(getConsolidatedByUserSelector)
  const partners = useEnabledPartners()
  const { input: claimInput } = useField('claim')
  const { input: claimReasonInput } = useField('claimReason')
  const { input: labelUrlInput } = useField('labelUrl')
  const { input: labelTypeInput } = useField('labelType')
  const { input: partner } = useField('partner')
  const { input: consolidationIdInput } = useField('consolidationId')
  const { input: requiresDeliverySignature } = useField<boolean>('requiresDeliverySignature')
  const { input: requiresAgeVerification } = useField<boolean>('requiresAgeVerification')
  const { input: consolidatedByInput } = useField('consolidatedBy')
  const { input: partnerInput } = useField('partner')
  const { input: trackingIdInput } = useField('trackingId')
  const { input: carrierInput } = useField('carrier')
  const [claimReasonValue, setClaimReasonValue] = useState<{
    key: ClaimReasons
    label: string
  } | null>(null)
  const [isClaimReasonOpen, setIsClaimReasonOpen] = useState(false)
  const [isDigitalLabel, setIsDigitalLabel] = useState(false)
  const [isLabelUploaded, setIsLabelUploaded] = useState(false)
  const [uploadLabelError, setUploadLabelError] = useState<string | null>(null)
  const [isRemovePackageModalOpen, setIsRemovePackageModalOpen] = useState(false)
  const [isCarrierManuallySelected, setIsCarrierManuallySelected] = useState(false)

  const onClaimReasonSelect = useCallback(
    (_, value) => {
      if (value) {
        claimReasonInput.onChange(value?.key)
        setClaimReasonValue(value)
        setIsClaimReasonOpen(false)
      } else {
        claimReasonInput.onChange(null)
        setClaimReasonValue(null)
      }
    },
    [claimReasonInput],
  )

  const handleClaimReasonOpen = useCallback(() => {
    setIsClaimReasonOpen(true)
  }, [])

  const handleClaimReasonClose = useCallback(() => {
    setIsClaimReasonOpen(false)
  }, [])

  const onChangeRequiresDeliverySignature = useCallback(
    val => {
      requiresDeliverySignature.onChange(val)
    },
    [requiresDeliverySignature],
  )

  const onChangeMinimumAgeVerification = useCallback(
    val => {
      requiresAgeVerification.onChange(val || null)
    },
    [requiresAgeVerification],
  )

  const onClearConsolidationIdSelect = useCallback(() => {
    dispatch(
      actions.scanBarcode.request({
        consolidationId: null,
      }),
    )

    consolidationIdInput.onChange(null)
    setIsRemovePackageModalOpen(false)
  }, [consolidationIdInput, dispatch])

  const onChangePartner = useCallback(
    val => {
      partner.onChange(val)
      onClearConsolidationIdSelect()
    },
    [onClearConsolidationIdSelect, partner],
  )

  const handleRemovePackageFromConsolidation = useCallback(() => {
    setIsRemovePackageModalOpen(true)
  }, [])

  const handleCloseRemovePackageModal = useCallback(() => {
    setIsRemovePackageModalOpen(false)
  }, [])

  const getConsolidatedByUser = useCallback(async () => {
    await createAsyncAction(
      dispatch,
      actions.getConsolidatedUserById.request({ id: currentPackage?.consolidatedBy }),
    )
  }, [currentPackage?.consolidatedBy, dispatch])

  const getConsolidationById = useCallback(async () => {
    try {
      await createAsyncAction(
        dispatch,
        actions.getConsolidationById.request({
          where: {
            consolidationId: consolidationIdInput.value,
          },
        }),
      )
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    }
  }, [consolidationIdInput.value, dispatch])

  const autoDetectCarrier = useCallback(
    value => {
      const detectedCarrier = carriers?.find(carrier => {
        if (carrier.regex) {
          const regex = new RegExp(carrier.regex)
          return regex.test(value.replace(/ /g, ''))
        }
      })

      carrierInput.onChange(detectedCarrier)
    },
    [carrierInput, carriers],
  )

  const clearTrackingId = useCallback(async () => {
    await createAsyncAction(
      dispatch,
      actions.clearPackageTrackingId.request({ id: currentPackage?.id }),
    )

    trackingIdInput.onChange(null)
  }, [dispatch, currentPackage?.id, trackingIdInput])

  const onNoLabelClick = useCallback(() => {
    const newTrackingId = currentPackage?.id
      ?.split('-')
      .join('')
      .toUpperCase()
    trackingIdInput.onChange(newTrackingId)
    autoDetectCarrier(newTrackingId)
  }, [autoDetectCarrier, currentPackage?.id, trackingIdInput])

  const onChangeTrackingId = useCallback(
    val => {
      trackingIdInput.onChange(val)

      if (!isCarrierManuallySelected) {
        autoDetectCarrier(val)
      }
    },
    [autoDetectCarrier, isCarrierManuallySelected, trackingIdInput],
  )

  const canEditRequirements = useMemo(
    () => userRole === cognitoGroups.admin || userRole === cognitoGroups.manager,
    [userRole],
  )

  const isConsolidationEnabled = useMemo(
    () =>
      partner?.value?.isConsolidationEnabled &&
      (AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION.includes(
        currentPackage?.status as PackageStatus,
      ) ||
        (NOT_AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION.includes(
          currentPackage?.status as PackageStatus,
        ) &&
          consolidationIdInput.value)),
    [consolidationIdInput.value, currentPackage?.status, partner?.value?.isConsolidationEnabled],
  )

  const lastScannedDate = useMemo(() => {
    if (!currentPackage?.scans.length) {
      return null
    }

    const lastScan = currentPackage.scans[0] as Scan

    return moment
      .unix(lastScan.createdAt)
      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
      .format('M/D/YY h:mm A z')
  }, [currentPackage?.scans])

  useEffect(() => {
    if (DIGITAL_LABELS.includes(labelTypeInput.value)) {
      setIsDigitalLabel(true)
    } else {
      setIsDigitalLabel(false)
    }
  }, [labelTypeInput.value])

  useEffect(() => {
    if (claimReasonInput.value) {
      CLAIM_REASONS.forEach((reason: { key: ClaimReasons; label: string }) => {
        if (reason.key === claimReasonInput.value) {
          setClaimReasonValue(reason)
        }
      })
    }
  }, [claimReasonInput.value])

  useEffect(() => {
    if ((barcodeScan?.consolidationId || barcodeScan?.scannedId) && isConsolidationEnabled) {
      consolidationIdInput.onChange(barcodeScan?.consolidationId || barcodeScan?.scannedId)
    }
  }, [
    barcodeScan?.consolidationId,
    barcodeScan?.scannedId,
    consolidationIdInput,
    isConsolidationEnabled,
  ])

  useEffect(() => {
    if (currentPackage?.requiresAgeVerification) {
      requiresAgeVerification.onChange(currentPackage?.requiresAgeVerification)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPackage?.requiresAgeVerification])

  useEffect(() => {
    if (currentPackage?.requiresDeliverySignature) {
      requiresDeliverySignature.onChange(currentPackage?.requiresDeliverySignature)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPackage?.requiresDeliverySignature])

  useEffect(() => {
    if (consolidatedByInput.value && consolidatedByInput.value !== consolidatedUser?.id) {
      getConsolidatedByUser()
    }
  }, [consolidatedByInput.value, getConsolidatedByUser, consolidatedUser?.id])

  useEffect(() => {
    if (consolidationIdInput.value && packageConsolidation?.id !== consolidationIdInput.value) {
      getConsolidationById()
    }
  }, [
    consolidationIdInput.value,
    consolidationIdInput.values,
    getConsolidationById,
    packageConsolidation?.id,
  ])

  useEffect(() => {
    if (trackingIdInput && !carrierInput.value) {
      autoDetectCarrier(trackingIdInput.value)
    }
  }, [trackingIdInput, carrierInput.value, autoDetectCarrier])

  useEffect(() => {
    if (carrierInput.value && typeof carrierInput.value === 'object') {
      setIsCarrierManuallySelected(true)
    }
  }, [carrierInput.value])

  return (
    <>
      {claimInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Claim Reason</p>
          <Field name="claimReason">
            {({ meta }) => (
              <AutocompleteInput
                options={CLAIM_REASONS || []}
                onChange={onClaimReasonSelect}
                readOnly={userRole !== cognitoGroups.admin}
                onOpen={handleClaimReasonOpen}
                onClose={handleClaimReasonClose}
                getOptionLabel={(option: { key: ClaimReasons; label: string }) => `${option.label}`}
                renderOption={(
                  events: HTMLAttributes<HTMLElement>,
                  option: { key: ClaimReasons; label: string },
                ) => (
                  <p
                    {...events}
                    key={option.label}
                    className={`${events.className} ${classes.optionSecondary}`}
                  >
                    {option.label}
                  </p>
                )}
                inputValue={claimReasonValue || ''}
                value={claimReasonValue || null}
                placeholder="Select claim reason..."
                noOptionsText="Select claim reason..."
                meta={meta}
                isOpen={isClaimReasonOpen}
              />
            )}
          </Field>
        </div>
      ) : null}

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Label</p>
        <div className={classes.labelTypeWrap}>
          <Field name="labelType">
            {({ input }) => (
              <RadioGroup {...input} row>
                {Object.keys(LABEL_TYPE_MAP).map((label: LabelType) => (
                  <FormControlLabel
                    key={label}
                    value={label}
                    classes={{
                      root: classes.formControlLabelRoot,
                      label: classes.formControlLabel,
                    }}
                    control={
                      <Radio
                        color="primary"
                        icon={<SelectorIcon />}
                        checkedIcon={<CheckedSelectorIcon />}
                      />
                    }
                    label={LABEL_TYPE_MAP[label]}
                  />
                ))}
              </RadioGroup>
            )}
          </Field>
        </div>
      </div>

      {isDigitalLabel ? (
        <div className={classes.detailsRow}>
          <p className={clsx(classes.inputHead, classes.labelTitle)}>
            {isLabelUploaded ? 'Label:' : 'Upload label:'}
          </p>
          <Field name="labelUrl">
            {({ meta }) => (
              <UploadLabelField
                labelType={labelTypeInput}
                labelUrl={labelUrlInput}
                setError={setUploadLabelError}
                setIsLabelUploaded={setIsLabelUploaded}
                error={meta.error || uploadLabelError}
              />
            )}
          </Field>
        </div>
      ) : null}

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Handoff Method</p>
        <Field name="method">
          {({ input, meta }) => (
            <Select
              value={input.value}
              onChange={input.onChange}
              values={TRIP_METHOD_MAP}
              className={classes.selectField}
              wrapperClassName={classes.selectWrapper}
              error={meta.error}
            />
          )}
        </Field>
      </div>

      <div className={classes.checkbox}>
        <div className={classes.swapCheckbox}>
          <p className={classes.inputHeadCheckbox}>Swap</p>
          <Field name="isSwap">
            {({ input }) => <Checkbox checked={input.value} onChange={input.onChange} />}
          </Field>
        </div>
        {currentPackage?.tripType === TripType.DROPOFF ? (
          <div className={clsx(classes.swapCheckbox, classes.requiresDeliverySignatureCheckbox)}>
            <p className={classes.inputHeadCheckbox}>Signature required</p>
            <Field name="requiresDeliverySignature">
              {({ input }) => (
                <Checkbox
                  disabled={!canEditRequirements}
                  checked={input.value}
                  onChange={onChangeRequiresDeliverySignature}
                />
              )}
            </Field>
          </div>
        ) : null}
        <p className={clsx(classes.inputHeadCheckbox, classes.boxRequiredTitle)}>Claim</p>
        <Field name="claim">
          {({ input }) => (
            <Checkbox
              disabled={userRole !== cognitoGroups.admin}
              checked={input.value}
              onChange={input.onChange}
            />
          )}
        </Field>
        {currentPackage?.verifiedDob ? (
          <>
            <p className={classes.inputHeadCheckbox}>Verified DOB</p>
            <Field name="verifiedDob">
              {({ input }) => (
                <p
                  className={clsx(classes.requiresAgeVerificationLabel, classes.inputHeadCheckbox)}
                >
                  {input.value}
                </p>
              )}
            </Field>
          </>
        ) : null}
      </div>

      {currentPackage?.tripType === TripType.DROPOFF ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Minimum age</p>
          <Field name="requiresAgeVerification">
            {({ input, meta }) => (
              <Input
                placeholder="Add minimum age"
                classes={{
                  textField: classes.input,
                }}
                onChange={onChangeMinimumAgeVerification}
                value={input.value}
                type="number"
                fullWidth
                disabled={!canEditRequirements}
                helperText={meta.error}
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
      ) : null}

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Expected Date</p>
        <Field name="expectedDate">
          {({ input }) => (
            <SingleDatePicker
              value={input.value}
              onChange={input.onChange}
              keyboardIcon={CalendarIcon}
              fullWidth
              className={classes.disabledDate}
              momentFormat="YYYY-MM-DD"
              disabled
            />
          )}
        </Field>
      </div>

      {lastScannedDate ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Last Scanned</p>
          <p className={classes.lastScannedDate}>{lastScannedDate}</p>
        </div>
      ) : null}

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Package Date</p>
        <Field name="packageDate">
          {({ input, meta }) => (
            <SingleDatePicker
              placeholder="Select date"
              value={input.value}
              onChange={input.onChange}
              keyboardIcon={CalendarIcon}
              className={classes.disabledDate}
              momentFormat="YYYY-MM-DD"
              error={meta.touched && meta.error}
              helperText={meta.error}
              disablePast
              fullWidth
              disabled
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Description</p>
        <Field name="description">
          {({ input, meta }) => (
            <Input
              placeholder="Package description, i.e pink shoes"
              classes={{
                textField: classes.descriptionInput,
              }}
              multiline
              onChange={input.onChange}
              value={input.value}
              fullWidth
              helperText={meta.error}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Partner</p>
        <Field name="partner">
          {({ input }) => (
            <WrappedSelect<'code', Partner>
              value={input.value as Partner}
              onChange={onChangePartner}
              values={partners as Partner[]}
              objectKey="code"
              placeholder="Choose partner..."
              fullWidth
            />
          )}
        </Field>
      </div>

      {isConsolidationEnabled ? (
        <div className={clsx(classes.selectWrap, classes.customCloseWrapper)}>
          <p className={classes.inputHead}>Consolidation ID:</p>
          <Field name="consolidationId">
            {({ input, meta }) => (
              <>
                <Input
                  placeholder="Enter your consolidation ID"
                  classes={{
                    input: classes.inputRoot,
                  }}
                  onChange={input.onChange}
                  value={input.value}
                  fullWidth
                  helperText={meta.error}
                  error={meta.touched && meta.error}
                />

                {input.value &&
                !NOT_AVAILABLE_PACKAGE_STATUSES_TO_ADD_TO_CONSOLIDATION.includes(
                  currentPackage?.status as PackageStatus,
                ) ? (
                  <div
                    className={classes.closeButton}
                    onClick={handleRemovePackageFromConsolidation}
                  >
                    <CloseIcon
                      className={classes.closeIcon}
                      strokeColor={PURE_RED}
                      width="18"
                      height="18"
                    ></CloseIcon>
                  </div>
                ) : null}
              </>
            )}
          </Field>
        </div>
      ) : null}

      {consolidatedUser?.email ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Consolidated By</p>
          <Field name="consolidatedByUserEmail">
            {() => <Input disabled value={consolidatedUser?.email} fullWidth />}
          </Field>
        </div>
      ) : null}

      {consolidationIdInput.value ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Consolidation Tracking Id</p>
          <Input
            placeholder={''}
            classes={{
              textField: classes.input,
            }}
            value={packageConsolidation?.trackingId || ''}
            disabled
            fullWidth
          />
        </div>
      ) : null}

      <div className={classes.selectWrap}>
        <div className={classes.inputHeaderWrap}>
          <p className={classes.inputHead}>Tracking Id</p>
          {partnerInput.value?.requiresReceiptTracking && !consolidationIdInput.value ? (
            <div className={classes.noLabelButton} onClick={onNoLabelClick}>
              GENERATE TRACKING
            </div>
          ) : null}
          <div className={classes.addButton} onClick={clearTrackingId}>
            <EraserIcon /> Clear
          </div>
        </div>
        <Field name="trackingId">
          {({ input, meta }) => (
            <Input
              placeholder={'Add tracking Id'}
              classes={{
                textField: classes.input,
              }}
              onChange={onChangeTrackingId}
              value={input.value}
              fullWidth
              helperText={meta.error || meta.submitError}
              error={
                (!meta.touched && meta.error) || (!meta.dirtySinceLastSubmit && meta.submitError)
              }
            />
          )}
        </Field>
      </div>

      <RemovePackageModal
        isOpen={isRemovePackageModalOpen}
        onClose={handleCloseRemovePackageModal}
        onConfirm={onClearConsolidationIdSelect}
      />
    </>
  )
}

export default memo(Information)
